import React, { useCallback, useContext, useMemo, useState } from 'react';
import { Select, Row, Col } from 'antd';
import { useTranslation } from 'react-i18next';
import useSwr from '@hooks/useSwr';
import KeycloakContext from '@providers/Keycloak/KeycloakContext';
import SessionContext from '@providers/Session/SessionContext';
import SelectCustomers from '@components/SelectCustomers';

import * as S from './styles';
import ContractConsumeCard from './ContractConsumeCard';

const SharedContracts = () => {
  const { t } = useTranslation('dashboard');
  const { keycloak } = useContext(KeycloakContext);
  const { hasCustomersRoles, isMotBusiness } = useContext(SessionContext);
  const customerType = keycloak?.idTokenParsed?.customerType;
  const isBroker = ['SUPERBROKER', 'BROKER'].includes(customerType ?? '');

  const { data: operatorsData } = useSwr('/service-proxy/mot-operators');

  const operatorsResponse = useMemo(
    () =>
      operatorsData?.map(({ name, id }: { name: string; id: number }) => ({
        label: name,
        value: id,
      })),
    [operatorsData],
  );

  const [operator, setOperator] = useState<number>();
  const [selectCustomerId, setSelectedCustomerId] = useState<number>();
  const [contractId, setContractId] = useState<number>();

  const { data: virtualPlans } = useSwr(
    `/service-proxy/inventory/MOT/dashboards/virtual-plans?showContracted=false${
      selectCustomerId ? `&customerId=${selectCustomerId}` : ''
    }`,
  );

  const { data: sharedContractsSummary } = useSwr(
    `/service-proxy/inventory/MOT/dashboards/shared-contracts-summary?showContracted=false${
      selectCustomerId ? `&customerId=${selectCustomerId}` : ''
    }${operator ? `&operator=${operator}` : ''}${
      contractId ? `&contractId=${contractId}` : ''
    }`,
  );

  const handleFilterChange = useCallback(
    (type: 'operator' | 'customer' | 'contract', value?: number) => {
      const setters = {
        operator: () => {
          setOperator(value);
          setSelectedCustomerId(undefined);
          setContractId(undefined);
        },
        customer: () => {
          setSelectedCustomerId(value);
          setContractId(undefined);
        },
        contract: () => setContractId(value),
      };

      setters[type]();
    },
    [],
  );

  return (
    <S.CardContainer
      title={t(`shared-contracts.${isMotBusiness ? 'shared-plans' : 'title'}`)}
      bodyStyle={{ padding: 24 }}
      extra={
        <S.CardActiveSimcards>
          <span>{`${t('shared-contracts.activeSimcards')}: `}</span>
          <strong>
            {t('shared-contracts.of', {
              one: sharedContractsSummary?.activeSimCardQty ?? 0,
              two: sharedContractsSummary?.simCardQty ?? 0,
            })}
          </strong>
        </S.CardActiveSimcards>
      }
    >
      <S.CardContainerFiltersRow gutter={[16, 16]}>
        {!isMotBusiness && (
          <Col lg={8} xs={24}>
            <p style={{ margin: 0 }}>{t('shared-contracts.operator')}</p>
            <Select
              value={operator}
              onChange={value => handleFilterChange('operator', value)}
              options={operatorsResponse}
              style={{ width: '100%' }}
              allowClear
            />
          </Col>
        )}
        {isBroker && hasCustomersRoles && (
          <Col lg={8} xs={24}>
            <span>
              {t(
                `shared-contracts.${isMotBusiness ? 'department' : 'customer'}`,
              )}
            </span>
            <SelectCustomers
              value={selectCustomerId && Number(selectCustomerId)}
              onChange={value => handleFilterChange('customer', value)}
              style={{ width: '100%' }}
              isMotBusiness={isMotBusiness}
            />
          </Col>
        )}

        {selectCustomerId && (
          <Col lg={8} xs={24}>
            <span>
              {t(`shared-contracts.${isMotBusiness ? 'plan' : 'contract'}`)}
            </span>
            <Select
              value={contractId}
              onChange={value => handleFilterChange('contract', value)}
              options={virtualPlans?.map((contract: any) => ({
                label: `${contract.id} - ${contract.planDesc}`,
                value: contract.id,
              }))}
              disabled={virtualPlans?.length === 0}
              placeholder={
                virtualPlans?.length === 0
                  ? t('shared-contracts.noContracts')
                  : ''
              }
              style={{ width: '100%' }}
              allowClear
            />
          </Col>
        )}
      </S.CardContainerFiltersRow>

      <Row gutter={[16, 16]}>
        <Col {...(isMotBusiness ? { span: 24 } : { lg: 12, xs: 24 })}>
          <ContractConsumeCard
            label="current"
            consumption={sharedContractsSummary?.currentConsumption}
            franchise={sharedContractsSummary?.currentFranchise}
            loading={!sharedContractsSummary}
          />
        </Col>

        {!isMotBusiness && (
          <Col lg={12} xs={24}>
            <ContractConsumeCard
              label="charged"
              consumption={sharedContractsSummary?.totalConsumption}
              franchise={sharedContractsSummary?.totalHiredFranchise}
              surplus={sharedContractsSummary?.exceedingConsumption}
              loading={!sharedContractsSummary}
            />
          </Col>
        )}
      </Row>
    </S.CardContainer>
  );
};

export default SharedContracts;
